import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { auth } from '../../lib/firebase';
import FeatureGate from './FeatureGate';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { tenantHeaders } from '../../lib/tenantSession';
// import AIVoiceMetrics from './Analytics/AIVoiceMetrics';
import { 
  Ticket, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  Users, 
  Phone,
  AlertTriangle,
  MessageSquare,
  BarChart3,
  Headphones,
  Building2,
  DollarSign,
  Activity
} from 'lucide-react';

const _OVERVIEW_INVALID_NAMES = new Set(['guest', 'unknown', 'anonymous', 'customer', 'undefined', 'null', 'none', '']);
const _FIREBASE_UID_RE = /^[A-Za-z0-9]{20,36}$/;
const _cleanCustomerDisplay = (name, id) => {
  if (name && !_OVERVIEW_INVALID_NAMES.has(name.toLowerCase()) && !_FIREBASE_UID_RE.test(name)) return name;
  if (id && !_OVERVIEW_INVALID_NAMES.has(id.toLowerCase()) && !_FIREBASE_UID_RE.test(id) && !id.startsWith('cust_')) return id;
  return 'Customer';
};

const OverviewDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBusiness, getBusinessStaff } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Real roster (auto-loaded by BusinessContext when the business loads); replaces the always-empty
  // getBusinessAnalytics() that fed a hardcoded Team Members count.
  const staffCount = (getBusinessStaff?.() || []).length;

  const [recentActivity, setRecentActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [perfMetrics, setPerfMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  // Live counts from operational endpoints — replace always-empty businessStats
  const [liveCounts, setLiveCounts] = useState({ tickets: 0, escalations: 0, conversations: 0, resolved: 0 });
  // Bumped after a successful New Ticket create so the overview re-fetches and the new ticket shows.
  const [refreshKey, setRefreshKey] = useState(0);

  const getToken = async () => {
    try { if (auth.currentUser) return await auth.currentUser.getIdToken(); } catch {}
    return localStorage.getItem('crm_access_token') || '';
  };

  useEffect(() => {
    // Fallback chain: context brandId → user.brand_id (snake_case from API) → user.brandId (camelCase)
    const businessId = currentBusiness?.brandId || user?.brand_id || user?.brandId;
    if (!businessId) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';

    const fetchActivity = async () => {
      setActivityLoading(true);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
        // IT/Integration is intentionally denied ticket endpoints by RBAC. Do NOT
        // call /tickets for IT — it would 403 on every normal page load. IT sees
        // only its allowed tenant metrics (conversations, escalations).
        const isIT = user?.role === 'it';
        const reqs = [
          // The LIST is a page (backend .to_list(100)) and drives Recent Activity only — never the
          // Conversations total. The authoritative total comes from the tenant-scoped count endpoint
          // below; using the page length pinned every tenant with >=100 threads at exactly 100.
          fetch(`${base}/api/v1/conversations/business/${businessId}`, { headers, signal: controller.signal }),
          fetch(`${base}/api/v1/escalations/business/${businessId}`, { headers, signal: controller.signal }),
          fetch(`${base}/api/v1/conversations/business/${businessId}/count`, { headers, signal: controller.signal }),
        ];
        if (!isIT) {
          reqs.push(fetch(`${base}/api/v1/tickets/business/${businessId}`, { headers, signal: controller.signal }));
        }
        const settled = await Promise.allSettled(reqs);
        const convRes = settled[0];
        const escRes = settled[1];
        const convCountRes = settled[2];
        const tickRes = isIT ? null : settled[3];
        clearTimeout(timeout);
        // Populate live stat counts while we have the data
        const counts = { tickets: 0, escalations: 0, conversations: 0, resolved: 0 };
        const activities = [];
        let convPage = [];
        if (convRes.status === 'fulfilled' && convRes.value.ok) {
          const d = await convRes.value.json();
          // Recent Activity only — the total is set from the count endpoint below.
          convPage = d.conversations || [];
          convPage.slice(0, 3).forEach(c => {
            activities.push({
              type: 'chat',
              action: `Conversation: ${_cleanCustomerDisplay(c.customer_name, c.customer_id)}`,
              time: c.updated_at || c.created_at,
              color: 'bg-blue-500',
            });
          });
        }
        // AUTHORITATIVE Conversations total — server-side count_documents, tenant-scoped, never a
        // page length. On failure we keep 0 rather than substituting the page size (no fabrication).
        if (convCountRes.status === 'fulfilled' && convCountRes.value.ok) {
          const c = await convCountRes.value.json();
          if (typeof c?.total === 'number') counts.conversations = c.total;
        }

        if (escRes.status === 'fulfilled' && escRes.value.ok) {
          const d = await escRes.value.json();
          counts.escalations = (d.escalations || []).length;
          (d.escalations || []).slice(0, 2).forEach(e => {
            activities.push({
              type: 'escalation',
              action: `Escalation: ${_cleanCustomerDisplay(e.customer_name, e.customer_id)} — ${e.status || 'pending'}`,
              time: e.created_at,
              color: 'bg-orange-500',
            });
          });
        }
        if (tickRes && tickRes.status === 'fulfilled' && tickRes.value.ok) {
          const d = await tickRes.value.json();
          const allTickets = d.tickets || [];
          counts.tickets = allTickets.length;
          counts.resolved = allTickets.filter(t => t.status === 'resolved').length;
          allTickets.slice(0, 2).forEach(t => {
            activities.push({
              type: 'ticket',
              action: `Ticket: ${_cleanCustomerDisplay(t.customer_name, t.customer_id)} — ${t.status || 'open'}`,
              time: t.created_at,
              color: 'bg-purple-500',
            });
          });
        }
        // Authoritative UNCAPPED counts (count_documents) — override the list-length approximations so a
        // pagination cap (.to_list(200/100)) is never presented as a total. Previously Total Tickets pinned
        // at 200, Resolved was a false 0 (resolved fell outside the newest-200 window), and Active
        // Escalations pinned at 200. Best-effort: on failure the list-based value stands (never a false 0).
        try {
          const [tc, ec] = await Promise.allSettled([
            isIT ? Promise.resolve(null) : fetch(`${base}/api/v1/tickets/business/${businessId}/counts`, { headers }),
            fetch(`${base}/api/v1/escalations/business/${businessId}/active-count`, { headers }),
          ]);
          if (tc && tc.status === 'fulfilled' && tc.value && tc.value.ok) {
            const d = await tc.value.json();
            if (typeof d.total === 'number') counts.tickets = d.total;
            if (typeof d.resolved === 'number') counts.resolved = d.resolved;
          }
          if (ec.status === 'fulfilled' && ec.value && ec.value.ok) {
            const d = await ec.value.json();
            if (typeof d.active === 'number') counts.escalations = d.active;
          }
        } catch (_e) { /* keep the list-based fallback; never a false zero */ }
        setLiveCounts(counts);
        activities.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
        setRecentActivity(activities.slice(0, 6));
      } catch (_) {
        clearTimeout(timeout);
        setRecentActivity([]);
      } finally {
        setActivityLoading(false);
      }
    };

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      try {
        const token = await getToken();
        const resp = await fetch(
          `${base}/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`,
          { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
        );
        if (resp.ok) {
          const d = await resp.json();
          setPerfMetrics(d);
        } else {
          setPerfMetrics({});
        }
      } catch (_) {
        setPerfMetrics({});
      } finally {
        setMetricsLoading(false);
      }
    };

    const fetchHealth = async () => {
      setHealthLoading(true);
      try {
        // Tenant-scoped, sanitized operational health for THIS business only. Authenticated + tenant
        // guarded server-side. Never the platform /system/health (owner-only infra) — that leaked
        // MongoDB/secrets/global integrations to tenants and is now SuperAdmin-guarded.
        const token = await getToken();
        const resp = await fetch(`${base}/api/v1/tenant-health/${businessId}`, {
          headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
        });
        if (resp.ok) {
          const d = await resp.json();
          setSystemHealth(d);
        } else {
          setSystemHealth({ status: 'unknown', components: [] });
        }
      } catch (_) {
        setSystemHealth({ status: 'unknown', components: [] });
      } finally {
        setHealthLoading(false);
      }
    };

    fetchActivity();
    fetchMetrics();
    fetchHealth();
  }, [currentBusiness?.brandId, user?.role, refreshKey]);

  // State for dialogs
  const [takeCallOpen, setTakeCallOpen] = useState(false);
  const [callQueue, setCallQueue] = useState(null);
  const [callQueueLoading, setCallQueueLoading] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  
  // New ticket form state
  const [ticketData, setTicketData] = useState({
    customer: '',
    title: '',
    category: '',
    priority: 'medium',
    description: ''
  });

  // Handle Take Call — read the SAME backend actionability source as the Unified Desktop
  // (operator-workspace incoming_queue), so Overview and Unified Desktop can never disagree. The count
  // reflects only ACTIONABLE incoming handoffs for this operator, not raw escalation history (which was
  // the source of the phantom "N in queue" that contradicted the desk).
  const handleTakeCall = async () => {
    setTakeCallOpen(true);
    setCallQueueLoading(true);
    setCallQueue(null);
    try {
      const businessId = currentBusiness?.brandId;
      if (!businessId) { setCallQueue([]); return; }
      const token = await getToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/operator-workspace/${businessId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (resp.ok) {
        const data = await resp.json();
        const actionable = (data.incoming_queue || []).filter(i => i && i.accept_allowed);
        setCallQueue(actionable.map(i => ({
          interaction_id: i.interaction_id,
          customer_name: i.customer_display_name || i.customer_name,
          reason: i.transcript_summary || i.escalation_reason,
          status: i.status,
        })));
      } else {
        setCallQueue([]);
      }
    } catch (_) {
      setCallQueue([]);
    } finally {
      setCallQueueLoading(false);
    }
  };

  const handleAcceptCall = () => {
    setTakeCallOpen(false);
    setCallQueue(null);
    navigate('/business/unified-desktop');
  };

  const handleRejectCall = () => {
    setCallQueue(null);
    setTakeCallOpen(false);
  };


  // Handle New Ticket creation — calls real backend
  const handleCreateTicket = async () => {
    if (!ticketData.customer || !ticketData.title || !ticketData.description) {
      toast({ title: t('dash.toast.missingInfo'), description: t('dash.toast.missingInfoDetail'), variant: "destructive" });
      return;
    }
    try {
      const token = await getToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/tickets/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
          body: JSON.stringify({
            customer_name: ticketData.customer,
            title: ticketData.title,
            description: ticketData.description,
            priority: ticketData.priority,
            category: ticketData.category || 'general',
            business_id: currentBusiness?.brandId || '',
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create ticket');
      }
      toast({ title: t('dash.toast.ticketCreated'), description: t('dash.toast.ticketCreatedDetail') });
      setTicketData({ customer: '', title: '', category: '', priority: 'medium', description: '' });
      setNewTicketOpen(false);
      setRefreshKey((k) => k + 1); // re-fetch so the new ticket appears in stats/activity

    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('dash.toast.ticketError'), variant: "destructive" });
    }
  };

  // Handler for View Reports button
  // EXEC-02: "View Reports" must reach a surface that can actually PRODUCE a report.
  // The executive branch sent them to /business/executive-dashboard, which has no export control of
  // any kind — runtime-confirmed: the button worked, it just landed on the one target that cannot
  // produce a report ("only navigates user to the Executive Dashboard with no option to download,
  // print or email"). Executive now falls through to /business/analytics, the existing report engine
  // (Executive PDF / Excel / PowerPoint / Raw Data CSV — all four runtime-proven). No report code is
  // touched and no new surface is created; Director and Manager routing are deliberately unchanged.
  const handleViewReports = () => {
    if (user?.role === 'director') {
      navigate('/business/strategy-dashboard');
    } else {
      navigate('/business/analytics');
    }
  };
  
  // If no business context and user is a business user, show loading
  if (!currentBusiness && user?.role !== 'customer') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('dash.loadingDashboard')}</h2>
          <p className="text-gray-600">{t('dash.loadingBusinessInfo')}</p>
        </div>
      </div>
    );
  }
  
  const getRoleBasedGreeting = (role) => {
    const greetings = {
      admin: t('dash.greeting.admin'),
      operator: t('dash.greeting.operator'),
      manager: t('dash.greeting.manager'),
      analyst: t('dash.greeting.analyst'),
      director: t('dash.greeting.director'),
      executive: t('dash.greeting.executive'),
      it: t('dash.greeting.it'),
      super_admin: t('dash.greeting.super_admin')
    };
    return greetings[role] || t('dash.greeting.default');
  };

  const getStatsCards = (role) => {
    const baseStats = [
      {
        title: t('dash.stat.totalTickets'),
        value: liveCounts.tickets,
        icon: Ticket,
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        change: null
      },
      {
        title: t('dash.stat.resolved'),
        value: liveCounts.resolved,
        icon: CheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-100',
        change: null
      },
      {
        title: t('dash.stat.activeEscalations'),
        value: liveCounts.escalations,
        icon: Clock,
        color: 'text-orange-600',
        bg: 'bg-orange-100',
        change: null
      },
      {
        title: t('dash.stat.conversations'),
        value: liveCounts.conversations,
        icon: TrendingUp,
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        change: null
      }
    ];

    // IT/Integration cannot access ticket endpoints — show only its allowed
    // tenant metrics (conversations + escalations), never ticket counts.
    const itStats = [
      {
        title: t('dash.stat.conversations'),
        value: liveCounts.conversations,
        icon: TrendingUp,
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        change: null
      },
      {
        title: t('dash.stat.activeEscalations'),
        value: liveCounts.escalations,
        icon: Clock,
        color: 'text-orange-600',
        bg: 'bg-orange-100',
        change: null
      }
    ];

    const roleSpecificStats = {
      operator: baseStats,
      manager: [
        ...baseStats,
        {
          title: t('dash.stat.teamMembers'),
          value: staffCount,
          icon: Users,
          color: 'text-indigo-600',
          bg: 'bg-indigo-100'
        }
      ],
      executive: baseStats,
      super_admin: baseStats,
      it: itStats
    };

    return roleSpecificStats[role] || baseStats;
  };

  const statsCards = getStatsCards(user?.role);

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('dash.welcome', { name: user?.name })}
        </h1>
        <p className="text-gray-600">{getRoleBasedGreeting(user?.role)}</p>
        {currentBusiness && (
          <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-900">{currentBusiness.businessName}</h3>
                <p className="text-green-700 text-sm">{currentBusiness.sector}</p>
              </div>
              {/* DR-008/gap-J: raw brand/business ID removed from tenant-facing header — the
                  business NAME above is the identity; the canonical id stays internal. */}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex space-x-4">
          {user?.role === 'operator' && (
            <>
              <Dialog open={takeCallOpen} onOpenChange={setTakeCallOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                    onClick={handleTakeCall}
                  >
                    <Headphones className="h-4 w-4 mr-2" />
                    {t('common.takeCall')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t('dash.callQueue.title')}</DialogTitle>
                    <DialogDescription>{t('dash.callQueue.subtitle', { business: currentBusiness?.businessName })}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {callQueueLoading ? (
                      <div className="text-center py-8">
                        <Headphones className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-pulse" />
                        <p className="text-gray-600">{t('dash.callQueue.checking')}</p>
                      </div>
                    ) : callQueue && callQueue.length > 0 ? (
                      <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-green-800">
                              {t('dash.callQueue.callsWaiting', { count: callQueue.length })}
                            </h4>
                            <Badge className="bg-green-100 text-green-800">{t('dash.callQueue.live')}</Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('dash.callQueue.customer')}</span>
                              <span className="font-medium">{callQueue[0].customer_name || 'Customer'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('dash.callQueue.reason')}</span>
                              <span className="font-medium">{callQueue[0].reason || callQueue[0].escalation_reason || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('dash.callQueue.status')}</span>
                              <span className="font-medium capitalize">{callQueue[0].status?.replace(/_/g, ' ') || '—'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <Button variant="outline" onClick={handleRejectCall} className="flex-1">
                            {t('dash.callQueue.dismiss')}
                          </Button>
                          <Button onClick={handleAcceptCall} className="flex-1 bg-green-600 hover:bg-green-700">
                            <Headphones className="h-4 w-4 mr-2" />
                            {t('dash.callQueue.acceptCall')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Headphones className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 font-medium">{t('dash.callQueue.empty')}</p>
                        <p className="text-sm text-gray-400 mt-1">{t('dash.callQueue.emptyDetail')}</p>
                        <Button className="mt-4" variant="outline" onClick={handleAcceptCall}>
                          {t('dash.callQueue.openDesktop')}
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
                <DialogTrigger asChild>
                  <FeatureGate label="ticket creation">
                    <Button variant="outline" data-testid="new-ticket-trigger-btn">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t('common.newTicket')}
                    </Button>
                  </FeatureGate>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{t('dash.newTicket.title')}</DialogTitle>
                    <DialogDescription>
                      {t('dash.newTicket.subtitle')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="customer">{t('dash.newTicket.customer')}</Label>
                      <Input
                        id="customer"
                        placeholder={t('dash.newTicket.customerPlaceholder')}
                        value={ticketData.customer}
                        onChange={(e) => setTicketData(prev => ({...prev, customer: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="title">{t('dash.newTicket.issueTitle')}</Label>
                      <Input
                        id="title"
                        placeholder={t('dash.newTicket.issueTitlePlaceholder')}
                        value={ticketData.title}
                        onChange={(e) => setTicketData(prev => ({...prev, title: e.target.value}))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">{t('dash.newTicket.category')}</Label>
                      <Select onValueChange={(value) => setTicketData(prev => ({...prev, category: value}))}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('dash.newTicket.selectCategory')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">{t('dash.newTicket.catTechnical')}</SelectItem>
                          <SelectItem value="billing">{t('dash.newTicket.catBilling')}</SelectItem>
                          <SelectItem value="general">{t('dash.newTicket.catGeneral')}</SelectItem>
                          <SelectItem value="account">{t('dash.newTicket.catAccount')}</SelectItem>
                          <SelectItem value="product">{t('dash.newTicket.catProduct')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="priority">{t('dash.newTicket.priority')}</Label>
                      <Select value={ticketData.priority} onValueChange={(value) => setTicketData(prev => ({...prev, priority: value}))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t('dash.newTicket.priorityLow')}</SelectItem>
                          <SelectItem value="medium">{t('dash.newTicket.priorityMedium')}</SelectItem>
                          <SelectItem value="high">{t('dash.newTicket.priorityHigh')}</SelectItem>
                          <SelectItem value="urgent">{t('dash.newTicket.priorityUrgent')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="description">{t('dash.newTicket.description')}</Label>
                      <Textarea
                        id="description"
                        placeholder={t('dash.newTicket.descriptionPlaceholder')}
                        value={ticketData.description}
                        onChange={(e) => setTicketData(prev => ({...prev, description: e.target.value}))}
                        rows={4}
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button variant="outline" onClick={() => setNewTicketOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleCreateTicket}>
                        {t('dash.newTicket.create')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
          {['manager', 'director', 'executive'].includes(user?.role) && (
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              onClick={handleViewReports}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              {t('common.viewReports')}
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
              onClick={() => navigate('/business/it-dashboard')}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {t('common.systemHealth')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card
              key={index}
              className={`bg-white/70 backdrop-blur-sm border-0 shadow-lg transition-shadow duration-200 ${stat.hardcoded ? 'cursor-default' : 'hover:shadow-xl'}`}
              onClick={stat.hardcoded ? () => toast({ title: stat.title, description: t('dash.notConnectedLong') }) : undefined}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    {stat.hardcoded && (
                      <div className="mt-1 mb-1">
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                          {t('dash.sampleData')}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">{t('dash.notConnectedShort')}</p>
                      </div>
                    )}
                    <p className={`text-2xl font-bold mt-1 ${stat.hardcoded ? 'opacity-40 text-gray-900' : 'text-gray-900'}`}>
                      {stat.value}
                    </p>
                    {!stat.hardcoded && stat.change && (
                      <div className="flex items-center mt-2">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            stat.change === 'live' ? 'bg-red-100 text-red-800' :
                            String(stat.change).startsWith('+') ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {stat.change === 'live' ? t('dash.live') : stat.change}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className={`p-3 rounded-full ${stat.bg} ${stat.hardcoded ? 'opacity-40' : ''}`}>
                    <IconComponent className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Voice Agent Metrics - Temporarily disabled for troubleshooting */}
      {/* {['admin', 'manager', 'operator'].includes(user?.role) && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">{t('fin.ov.aiVoicePerf')}</h2>
          <AIVoiceMetrics compact={true} />
        </div>
      )} */}

      {/* Role-specific sections */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Activity — real backend data */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle>{t('dash.recentActivity')}</CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <div className="flex items-center justify-center py-6 text-gray-400">
                <Activity className="h-4 w-4 animate-spin mr-2" />
                {t('dash.loadingActivity')}
              </div>
            ) : (recentActivity === null || recentActivity.length === 0) ? (
              <div className="text-center py-6">
                <Activity className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{t('dash.noActivity')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.color || 'bg-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.action}</p>
                      <p className="text-xs text-gray-500">
                        {item.time ? formatInTenantTz(item.time) : '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Metrics — real data from ai-chat analytics */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle>{t('dash.performanceMetrics')}</CardTitle>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="flex items-center justify-center py-6 text-gray-400">
                <BarChart3 className="h-4 w-4 animate-spin mr-2" />
                {t('dash.loadingMetrics')}
              </div>
            ) : perfMetrics === null ? null : (() => {
              const autoRate = perfMetrics.auto_resolution_rate ?? 0;
              const escRate = perfMetrics.escalation_rate ?? 0;
              const total = perfMetrics.total_conversations ?? 0;
              return (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('dash.autoResolutionRate')}</span>
                    <span className="text-sm font-bold text-green-600">{autoRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full"
                      style={{ width: `${Math.min(autoRate, 100)}%` }} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('dash.escalationRate')}</span>
                    <span className="text-sm font-bold text-orange-600">{escRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-orange-400 to-orange-600 h-2 rounded-full"
                      style={{ width: `${Math.min(escRate, 100)}%` }} />
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 pt-1 border-t">
                    <span>{t('dash.totalConversationsAnalyzed')}</span>
                    <span className="font-semibold text-gray-700">{total}</span>
                  </div>
                  {total === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">{t('dash.noConversationData')}</p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Business Health — tenant-scoped operational status only (never platform/owner infra) */}
      {['admin', 'administrator', 'manager', 'operator', 'it', 'analyst', 'director', 'executive'].includes(user?.role) && (
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg mt-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                {t('dash.businessHealth')}
              </CardTitle>
              {systemHealth && (
                <Badge className={
                  systemHealth.status === 'healthy' ? 'bg-green-100 text-green-800' :
                  systemHealth.status === 'degraded' ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-600'
                }>
                  {systemHealth.status === 'healthy' ? t('dash.healthy') :
                   systemHealth.status === 'degraded' ? t('dash.degraded') :
                   systemHealth.status === 'unavailable' ? t('dash.unavailable') :
                   (systemHealth.status || 'unknown').toUpperCase()}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <div className="flex items-center justify-center py-6 text-gray-400">
                <Activity className="h-4 w-4 animate-spin mr-2" />
                {t('dash.checkingServices')}
              </div>
            ) : systemHealth === null ? null : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(systemHealth.components || []).map((c, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      c.status === 'healthy' ? 'bg-green-500' :
                      c.status === 'degraded' ? 'bg-amber-500' :
                      'bg-gray-400'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{c.name}</p>
                      <p className={`text-xs ${
                        c.status === 'healthy' ? 'text-green-600' :
                        c.status === 'degraded' ? 'text-amber-600' :
                        'text-gray-500'
                      }`}>
                        {c.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {systemHealth && systemHealth.status === 'degraded' && (
              <p className="text-xs text-amber-600 mt-3">{t('dash.someServicesDegraded')}</p>
            )}
            {systemHealth && systemHealth.checked_at && (
              <p className="text-xs text-gray-400 mt-3 text-right">
                {t('dash.lastChecked')} {formatInTenantTz(systemHealth.checked_at, { timeStyle: 'medium' })}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OverviewDashboard;